import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState, useCallback, forwardRef, useImperativeHandle, useEffect, useRef, } from "react";
import { Space, Typography, Divider, Tree, Button, List, Input, Modal, Select, message, } from "antd";
import { ClearOutlined, DatabaseOutlined, ThunderboltOutlined, SettingOutlined, LoadingOutlined, ReloadOutlined, } from "@ant-design/icons";
import ContainerPanel from "../containers/ContainerPanel";
import ContainerModal from "../containers/ContainerModal";
import CompsForm from "./CompsForm";
import { useAiComponentHook } from "../hooks/useAiComponentHook";
import axios from "axios";
import "./CompsAi.css";
import { COMPONENT_OUTLINE_BORDER, COMPONENT_SPLIT_BORDER_BOTTOM, } from "../theme/componentBorders";
const { Title, Paragraph, Text } = Typography;
/** 从 AI 返回的 results（按 methodId 聚合）中提取用于展示的纯文本，只取 data.content */
function getDisplayContentFromResults(results) {
    if (!results || typeof results !== "object")
        return "";
    const texts = [];
    Object.values(results).forEach((r) => {
        const data = r?.data;
        if (data?.content != null && typeof data.content === "string") {
            texts.push(data.content.trim());
        }
    });
    return texts.join("\n\n");
}
/** 若 content 是之前存的完整 JSON，解析并只返回 data.content 的拼接；否则返回原字符串 */
function getDisplayContent(messageContent) {
    const s = messageContent?.trim() || "";
    if (!s)
        return "";
    if (s.startsWith("{") && s.includes('"data"')) {
        try {
            const parsed = JSON.parse(s);
            return getDisplayContentFromResults(parsed) || s;
        }
        catch {
            return s;
        }
    }
    return s;
}
/** 将内容中的 **...** 渲染为加粗，保留换行；不引入 HTML，安全 */
function renderContentWithFormatting(text) {
    const s = text?.trim() || "";
    if (!s)
        return null;
    const parts = s.split("**");
    if (parts.length <= 1)
        return s;
    return parts.map((part, i) => i % 2 === 1 ? (_jsx(Text, { strong: true, children: part }, i)) : (_jsx(React.Fragment, { children: part }, i)));
}
/**
 * 从 payload 中抽取「结构」：保留字段名与层级，将具体值替换为类型占位（null/[]/""），
 * 用于编辑传入数据弹窗中仅展示结构、不展示数据，便于用户根据结构决定是否保存为待响应事件。
 */
function getPayloadStructure(val) {
    if (val === null || val === undefined)
        return null;
    if (Array.isArray(val)) {
        if (val.length === 0)
            return [];
        return [getPayloadStructure(val[0])];
    }
    if (typeof val === "object") {
        const obj = val;
        const out = {};
        for (const k of Object.keys(obj)) {
            out[k] = getPayloadStructure(obj[k]);
        }
        return out;
    }
    if (typeof val === "string")
        return "";
    if (typeof val === "number")
        return null;
    if (typeof val === "boolean")
        return null;
    return null;
}
/**
 * 价格建议（房态动态定价）完整传入数据结构默认值
 * 当无样本或样本结构不完整时展示，避免「传入的数据结构」出现 dataSource[0] 为空对象等不全情况
 */
const DEFAULT_PRICE_SUGGESTION_INPUT_STRUCTURE = {
    storeId: "",
    componentType: "room-status",
    data: {
        timeDirection: "past",
        current: [
            {
                id: "",
                label: "",
                progress: null,
                quantity: null,
                quantityOpen: null,
                priceData: [],
                normalizedReferenceLines: [],
                isStoreTotal: null,
                progressColor: "",
                chartColor: "",
            },
        ],
        last7Days: null,
        last30Days: null,
        last12Months: null,
        priceSuggestionContext: {
            currentTime: "",
            overallOccupancy: null,
            remainingTotalRooms: null,
            orderFlowSpeed: "",
            externalFactors: "",
            roomStrategies: {},
            hasFollower: null,
            hasFollowed: null,
        },
    },
};
/** 判断数组首元素是否为空对象（无键或键为空） */
function isEmptyObject(v) {
    if (v == null || typeof v !== "object")
        return true;
    return Object.keys(v).length === 0;
}
/**
 * 当传入结构不完整时（如 current 或 current.dataSource 首项为空对象），用默认结构补全
 */
function ensureCompletePayloadStructure(raw, defaultStructure) {
    if (raw == null || typeof raw !== "object")
        return raw;
    const o = raw;
    const data = o.data;
    if (!data || typeof data !== "object")
        return raw;
    const defaultData = defaultStructure?.data;
    const defaultCurrent = defaultData?.current;
    const defaultItem = Array.isArray(defaultCurrent)
        ? defaultCurrent[0]
        : undefined;
    let current = data.current;
    // current 为数组且首项为空 → 用默认 current 替换
    if (Array.isArray(current) && defaultItem != null) {
        if (current.length === 0 || isEmptyObject(current[0])) {
            return { ...o, data: { ...data, current: defaultCurrent } };
        }
    }
    // current 为 { dataSource: [...] } 且 dataSource 首项为空 → 用默认项补全 dataSource
    if (current && typeof current === "object") {
        const cur = current;
        const dataSource = cur.dataSource;
        if (Array.isArray(dataSource) && defaultItem != null) {
            if (dataSource.length === 0 || isEmptyObject(dataSource[0])) {
                return {
                    ...o,
                    data: { ...data, current: { ...cur, dataSource: defaultCurrent } },
                };
            }
        }
    }
    return raw;
}
/**
 * 从结构对象中收集所有字段路径（用于「选择数据」「脱敏字段」等，与具体数据无关）
 * 例如 { a: "", b: { c: "" } } => ["a", "b", "b.c"]
 */
function getFieldsFromStructure(structure, prefix = "") {
    if (structure === null || structure === undefined)
        return [];
    if (Array.isArray(structure)) {
        return getFieldsFromStructure(structure[0], prefix);
    }
    if (typeof structure === "object") {
        const obj = structure;
        return Object.keys(obj).flatMap((k) => {
            const key = prefix ? `${prefix}.${k}` : k;
            const v = obj[k];
            if (v !== null && typeof v === "object") {
                const nested = getFieldsFromStructure(v, key);
                return nested.length > 0 ? [key, ...nested] : [key];
            }
            return [key];
        });
    }
    return [];
}
/** 添加/编辑思考方式对话框：核心问题、思维要求与禁止、必做操作、返回格式结构、返回格式要求与禁止，保存到数据库 */
function AddThinkingMethodDialog({ visible, onClose, onSaved, pageId, eventHash, token, apiBaseUrl, existingMethods, samplePayload, initialMethod, }) {
    const [loading, setLoading] = useState(false);
    const [datasets, setDatasets] = useState([]);
    const [selectedDataset, setSelectedDataset] = useState("");
    const [selectedDataFields, setSelectedDataFields] = useState([]);
    const [displayName, setDisplayName] = useState("");
    const [briefDesc, setBriefDesc] = useState("");
    const [question, setQuestion] = useState("");
    /** 思维要求与禁止（合并原「思考方式需求」+「思考方式避免」） */
    const [thinkingConstraints, setThinkingConstraints] = useState("");
    const [requiredActions, setRequiredActions] = useState("");
    const [returnStructure, setReturnStructure] = useState("");
    /** 返回格式要求与禁止（合并原「返回格式需求」+「返回格式避免」） */
    const [returnConstraints, setReturnConstraints] = useState("");
    // 脱敏字段（多选）
    const [sensitiveFields, setSensitiveFields] = useState([]);
    // 打开时：有 initialMethod 则回填表单，否则清空表单
    useEffect(() => {
        if (!visible)
            return;
        if (initialMethod) {
            setDisplayName(initialMethod.display_name ?? "");
            setBriefDesc(initialMethod.brief_desc ?? "");
            setQuestion(initialMethod.detail?.question ?? "");
            const aff = Array.isArray(initialMethod.detail?.affirmative_words)
                ? initialMethod.detail.affirmative_words.join("，")
                : "";
            const neg = Array.isArray(initialMethod.detail?.negative_words)
                ? initialMethod.detail.negative_words.join("，")
                : "";
            setThinkingConstraints(aff || neg ? `要求：${aff}\n禁止：${neg}` : "");
            setRequiredActions(Array.isArray(initialMethod.detail?.required_actions)
                ? initialMethod.detail.required_actions.join("\n")
                : "");
            setReturnStructure(initialMethod.return_format?.format_detail?.structure ??
                (initialMethod.display_name === "价格建议"
                    ? "房型:当前价,建议价,理由"
                    : ""));
            const retAff = Array.isArray(initialMethod.return_format?.format_detail?.affirmative_words)
                ? initialMethod.return_format.format_detail.affirmative_words.join("，")
                : "";
            const retNeg = Array.isArray(initialMethod.return_format?.format_detail?.negative_words)
                ? initialMethod.return_format.format_detail.negative_words.join("，")
                : "";
            setReturnConstraints(retAff || retNeg ? `要求：${retAff}\n禁止：${retNeg}` : "");
            setSensitiveFields(Array.isArray(initialMethod.sensitive_fields)
                ? initialMethod.sensitive_fields
                : []);
            setSelectedDataFields(Array.isArray(initialMethod.selected_data_fields)
                ? initialMethod.selected_data_fields
                : []);
        }
        else {
            setDisplayName("");
            setBriefDesc("");
            setQuestion("");
            setThinkingConstraints("");
            setRequiredActions("");
            setReturnStructure("");
            setReturnConstraints("");
            setSensitiveFields([]);
            setSelectedDataFields([]);
        }
    }, [visible, initialMethod]);
    useEffect(() => {
        if (visible) {
            setLoading(true);
            const url = apiBaseUrl
                ? `${apiBaseUrl.replace(/\/$/, "")}/api/ai/get-datasets`
                : "/api/ai/get-datasets";
            (typeof fetch !== "undefined" ? fetch : globalThis.fetch)(url)
                .then((r) => r.json())
                .then((res) => {
                setDatasets(Array.isArray(res?.data) ? res.data : []);
            })
                .catch(() => setDatasets([]))
                .finally(() => setLoading(false));
        }
    }, [visible, apiBaseUrl]);
    const currentDataset = datasets.find((d) => d.dataset_id === selectedDataset);
    const dataFields = currentDataset?.data_fields ?? [];
    // 左侧：只展示数据结构（与具体数据无关）；无样本或结构不完整时用默认完整结构，避免 dataSource[0] 为空
    const payloadStructure = useMemo(() => {
        const raw = getPayloadStructure(samplePayload ?? {});
        const ensured = ensureCompletePayloadStructure(raw, DEFAULT_PRICE_SUGGESTION_INPUT_STRUCTURE);
        const isEmpty = !samplePayload ||
            (typeof raw === "object" &&
                raw !== null &&
                Object.keys(raw).length === 0);
        return isEmpty ? DEFAULT_PRICE_SUGGESTION_INPUT_STRUCTURE : ensured;
    }, [samplePayload]);
    const structureFields = useMemo(() => getFieldsFromStructure(payloadStructure), [payloadStructure]);
    const effectiveDataFields = samplePayload && structureFields.length > 0 ? structureFields : dataFields;
    const handleSave = useCallback(() => {
        if (!eventHash || !pageId) {
            message.error("缺少 eventHash 或 pageId，无法保存思考方式，请先保存传送信息。");
            return;
        }
        if (!displayName?.trim()) {
            message.error("请填写方法名称");
            return;
        }
        if (!question?.trim()) {
            message.error("请填写核心问题");
            return;
        }
        const methodId = initialMethod?.method_id ?? `method-${Date.now()}`;
        const parseConstraints = (text) => {
            const req = [];
            const neg = [];
            if (!text?.trim())
                return { req, neg };
            const banIdx = text.indexOf("禁止：");
            const reqPart = banIdx >= 0 ? text.slice(0, banIdx) : text;
            const negPart = banIdx >= 0 ? text.slice(banIdx + 3) : "";
            const reqStr = reqPart.replace(/^要求：?/, "").trim();
            req.push(...reqStr
                .split(/[,，\n]/)
                .map((w) => w.trim())
                .filter(Boolean));
            neg.push(...negPart
                .split(/[,，\n]/)
                .map((w) => w.trim())
                .filter(Boolean));
            return { req, neg };
        };
        const { req: affWords, neg: negWords } = parseConstraints(thinkingConstraints);
        const { req: retAffWords, neg: retNegWords } = parseConstraints(returnConstraints);
        const baseMethod = {
            method_id: methodId,
            display_name: displayName || "未命名思考方法",
            brief_desc: briefDesc,
            detail: {
                question,
                affirmative_words: affWords,
                negative_words: negWords,
                required_actions: requiredActions
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean),
            },
            return_format: {
                format_name: displayName || "未命名",
                format_brief: briefDesc,
                format_detail: {
                    structure: returnStructure,
                    affirmative_words: retAffWords,
                    negative_words: retNegWords,
                },
            },
        };
        // 附加脱敏字段、选择数据字段（后端可选择性使用）
        const method = {
            ...baseMethod,
            sensitive_fields: sensitiveFields,
            selected_data_fields: selectedDataFields,
        };
        setLoading(true);
        const url = apiBaseUrl
            ? `${apiBaseUrl.replace(/\/$/, "")}/api/ai/update-methods`
            : "/api/ai/update-methods";
        const nextMethods = initialMethod
            ? existingMethods.map((m) => m.method_id === initialMethod.method_id ? method : m)
            : [...existingMethods, method];
        fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: token || "",
            },
            body: JSON.stringify({
                eventHash,
                pageId,
                thinkingMethods: nextMethods,
                token,
            }),
        })
            .then(async (r) => {
            if (!r.ok) {
                const text = await r.text().catch(() => "");
                throw new Error(text || r.statusText || "服务器返回错误");
            }
            return r.json();
        })
            .then(() => {
            message.success("思考方式已保存");
            onSaved();
            onClose();
        })
            .catch((e) => {
            // eslint-disable-next-line no-console
            console.error("保存思考方法失败:", e);
            message.error(`保存思考方式失败：${e?.message || "请稍后重试或联系后台同学检查接口"}`);
        })
            .finally(() => setLoading(false));
    }, [
        displayName,
        briefDesc,
        question,
        thinkingConstraints,
        requiredActions,
        returnStructure,
        returnConstraints,
        sensitiveFields,
        selectedDataFields,
        eventHash,
        pageId,
        token,
        existingMethods,
        initialMethod,
        apiBaseUrl,
        onSaved,
        onClose,
    ]);
    const formItemLabelStyle = {
        fontSize: 12,
        color: "#595959",
        flexShrink: 0,
        marginBottom: 4,
    };
    const formItemWrapperStyle = {
        display: "flex",
        flexDirection: "column",
        gap: 4,
    };
    return (_jsx(Modal, { title: initialMethod ? "编辑思考方式" : "添加思考方式", open: visible, onCancel: onClose, onOk: handleSave, confirmLoading: loading, width: 1200, destroyOnClose: true, styles: {
            body: {
                padding: "24px 32px",
                height: "auto",
            },
        }, children: _jsxs("div", { style: {
                display: "flex",
                gap: 20,
                alignItems: "stretch",
                height: 760,
                minHeight: 760,
                maxHeight: 760,
                overflow: "hidden",
            }, children: [_jsxs("div", { style: {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                        minWidth: 0,
                        minHeight: 0,
                        overflow: "hidden",
                    }, children: [_jsxs("div", { style: formItemWrapperStyle, children: [_jsxs("div", { style: formItemLabelStyle, children: [_jsx("span", { style: { color: "#ff4d4f", marginRight: 4 }, children: "*" }), "\u65B9\u6CD5\u540D\u79F0"] }), _jsx(Input, { placeholder: "\u8BF7\u8F93\u5165\u65B9\u6CD5\u540D\u79F0\uFF08\u5FC5\u586B\uFF09", value: displayName, onChange: (e) => setDisplayName(e.target.value) })] }), _jsxs("div", { style: formItemWrapperStyle, children: [_jsx("div", { style: formItemLabelStyle, children: "\u7B80\u8981\u63CF\u8FF0" }), _jsx(Input.TextArea, { placeholder: "\u8BF7\u8F93\u5165\u7B80\u8981\u63CF\u8FF0", value: briefDesc, onChange: (e) => setBriefDesc(e.target.value), rows: 2 })] }), _jsxs("div", { style: {
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 0,
                                gap: 12,
                            }, children: [_jsx("div", { style: {
                                        fontWeight: 500,
                                        fontSize: 13,
                                        flexShrink: 0,
                                    }, children: "\u6570\u636E\u7ED3\u6784" }), _jsxs("div", { style: {
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 8,
                                        flex: 1,
                                        minHeight: 0,
                                        borderRadius: 8,
                                        border: COMPONENT_OUTLINE_BORDER,
                                        background: "#fafafa",
                                        padding: 16,
                                        overflow: "hidden",
                                    }, children: [_jsx("div", { style: {
                                                fontWeight: 500,
                                                fontSize: 12,
                                                color: "#595959",
                                                flexShrink: 0,
                                            }, children: "\u4F20\u5165\u7684\u6570\u636E\u7ED3\u6784" }), _jsx("pre", { style: {
                                                margin: 0,
                                                fontSize: 11,
                                                fontFamily: "Menlo, Monaco, Consolas, monospace",
                                                lineHeight: 1.4,
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                flex: 1,
                                                minHeight: 0,
                                                overflow: "auto",
                                            }, children: JSON.stringify(payloadStructure, null, 2) }), !samplePayload && (_jsx("div", { style: {
                                                fontSize: 11,
                                                color: "#8c8c8c",
                                                marginTop: 4,
                                                flexShrink: 0,
                                            }, children: "\u6682\u65E0\u70B9\u51FB\u6837\u672C\u65F6\u5C55\u793A\u9ED8\u8BA4\u5B8C\u6574\u7ED3\u6784\uFF08\u4EF7\u683C\u5EFA\u8BAE/\u623F\u6001\uFF09" }))] })] })] }), _jsxs("div", { style: {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                        minWidth: 0,
                        borderLeft: COMPONENT_SPLIT_BORDER_BOTTOM,
                        borderRight: COMPONENT_SPLIT_BORDER_BOTTOM,
                        paddingLeft: 20,
                        paddingRight: 20,
                    }, children: [_jsxs("div", { style: formItemWrapperStyle, children: [_jsxs("div", { style: formItemLabelStyle, children: [_jsx("span", { style: { color: "#ff4d4f", marginRight: 4 }, children: "*" }), "\u6838\u5FC3\u95EE\u9898"] }), _jsx(Input.TextArea, { placeholder: "\u8BF7\u8F93\u5165\u8BA9 AI \u89E3\u51B3\u7684\u6838\u5FC3\u95EE\u9898\uFF08\u5FC5\u586B\uFF09", value: question, onChange: (e) => setQuestion(e.target.value), rows: 17 })] }), _jsxs("div", { style: formItemWrapperStyle, children: [_jsx("div", { style: formItemLabelStyle, children: "\u601D\u7EF4\u8981\u6C42\u4E0E\u7981\u6B62" }), _jsx(Input.TextArea, { placeholder: "要求：判断、区分、对比…（逗号分隔）\n禁止：猜测、一刀切、无依据…（逗号分隔）", value: thinkingConstraints, onChange: (e) => setThinkingConstraints(e.target.value), rows: 5 })] }), _jsxs("div", { style: formItemWrapperStyle, children: [_jsx("div", { style: formItemLabelStyle, children: "\u5FC5\u505A\u64CD\u4F5C" }), _jsx(Input.TextArea, { placeholder: "例如：\n1. 提取 amountList 字段\n2. 累加所有数字", value: requiredActions, onChange: (e) => setRequiredActions(e.target.value), rows: 6 })] }), !samplePayload && (_jsxs("div", { style: formItemWrapperStyle, children: [_jsx("div", { style: formItemLabelStyle, children: "\u9009\u62E9\u6570\u636E\u96C6" }), _jsx(Select, { placeholder: "\u8BF7\u9009\u62E9\u6570\u636E\u96C6", value: selectedDataset || undefined, onChange: setSelectedDataset, style: { width: "100%" }, options: datasets.map((d) => ({
                                        label: d.dataset_name,
                                        value: d.dataset_id,
                                    })) })] }))] }), _jsxs("div", { style: {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                        minWidth: 0,
                    }, children: [effectiveDataFields.length > 0 && (_jsxs("div", { style: formItemWrapperStyle, children: [_jsx("div", { style: formItemLabelStyle, children: "\u9009\u62E9\u6570\u636E" }), _jsx(Select, { mode: "multiple", allowClear: true, placeholder: "\u8BF7\u9009\u62E9\u8981\u53C2\u4E0E\u5206\u6790\u7684\u5B57\u6BB5", value: selectedDataFields, onChange: (values) => setSelectedDataFields(values), style: { width: "100%" }, options: effectiveDataFields.map((field) => ({
                                        label: field,
                                        value: field,
                                    })) })] })), _jsxs("div", { style: formItemWrapperStyle, children: [_jsx("div", { style: formItemLabelStyle, children: "\u8131\u654F\u5B57\u6BB5" }), _jsx(Select, { mode: "multiple", allowClear: true, placeholder: "\u9009\u62E9\u9700\u8981\u8131\u654F\u7684\u5B57\u6BB5", value: sensitiveFields, onChange: (values) => setSensitiveFields(values), style: { width: "100%" }, options: effectiveDataFields.map((field) => ({
                                        label: field,
                                        value: field,
                                    })) })] }), _jsxs("div", { style: formItemWrapperStyle, children: [_jsx("div", { style: formItemLabelStyle, children: "\u8FD4\u56DE\u683C\u5F0F\u7ED3\u6784" }), _jsx(Input.TextArea, { placeholder: "\u4F8B\u5982\uFF1A\u623F\u578B:\u5F53\u524D\u4EF7,\u5EFA\u8BAE\u4EF7,\u7406\u7531", value: returnStructure, onChange: (e) => setReturnStructure(e.target.value), rows: 12 })] }), _jsxs("div", { style: formItemWrapperStyle, children: [_jsx("div", { style: formItemLabelStyle, children: "\u8FD4\u56DE\u683C\u5F0F\u8981\u6C42\u4E0E\u7981\u6B62" }), _jsx(Input.TextArea, { placeholder: "要求：具体数值、明确理由…（逗号分隔）\n禁止：模糊表述、无依据…（逗号分隔）", value: returnConstraints, onChange: (e) => setReturnConstraints(e.target.value), rows: 5 })] })] })] }) }));
}
/**
 * CompsAi 组件
 * 只负责显示和选择，不负责AI调用
 * AI调用由上层通过hook处理
 *
 * 新设计（apiBaseUrl + onResult + ref）：通过 ref.receiveMessage(msg) 发送 StandardAiComponentMessage，
 * 内部走 get-thinking-methods / get-cache / save-cache / call-model 统一接口，结果由 onResult 回调。
 *
 * 上半部分：以树形结构展示多层数据（支持任意层级）
 * 下半部分：AI返回的消息列表
 */
const CompsAiInner = ({ treeData, onNodeClick, onThinkingModeClick, aiMessages: aiMessagesProp, loading: loadingProp, title = "AI 助手", style, height = "100%", onClearMessages, onClearAll, onSettingsClick, defaultExpandAll = true, renderNodeTitle, apiBaseUrl, onResult, 
// 旧接口兼容属性
dataSource, thinkingModes, onFetchData, onSelectionChange, 
// 新设计：从数据库获取思考模式
pageId, microServiceId, configApiBaseUrl, skipPageConfigLoad, 
// 三部分之「可选择的思考方式」：是否可编辑、添加/编辑回调
editable = false, onAddThinkingMode, onEditThinkingMode, }, ref) => {
    // ==================== 新设计：AI 结果展示（受控 / 非受控） ====================
    // - 触发动作：ref.receiveMessage(msg)
    // - 展示状态：优先使用外部传入的 aiMessages/loading；未传时由组件内部维护
    const [innerAiMessages, setInnerAiMessages] = useState([]);
    const [innerLoading, setInnerLoading] = useState(false);
    const aiMessages = aiMessagesProp ?? innerAiMessages;
    const loading = loadingProp ?? innerLoading;
    // 新设计：用于「传入的信息」区域展示最近一次消息
    const [lastIncomingMessage, setLastIncomingMessage] = useState(null);
    // 用 ref 存一份最新消息，点击思考方式时用 ref 保证拿到带完整 payload 的消息（避免 state 未刷新导致只传结构不传数据）
    const lastIncomingMessageRef = useRef(null);
    // 用于 ref.clearSelection：切换组件/门店时先清理选择状态
    const clearSelectionFnRef = useRef(() => { });
    // 「传入的信息」编辑：允许基于最近一次消息编辑 payload
    const [editedPayloadOverride, setEditedPayloadOverride] = useState(null);
    const [editPayloadModalVisible, setEditPayloadModalVisible] = useState(false);
    const [editPayloadSaving, setEditPayloadSaving] = useState(false);
    const editPayloadFormRef = useRef(null);
    // 新设计：从数据库查询到的思考方法（基于 lastIncomingMessage）
    const [fetchedThinkingModes, setFetchedThinkingModes] = useState([]);
    // 新设计：根据传参显示的组件名称（来自 get-component-info 或 payload.metadata.label）
    const [displayComponentName, setDisplayComponentName] = useState("");
    // 记录当前展示名称对应的组件（同一组件再次点击时不拿 payload 英文覆盖已取得的中文名）
    const displayNameComponentKeyRef = useRef(null);
    // 当前组件对应的事件是否已在后端存在（用于控制是否允许添加思考方式）
    const [hasExistingEventForComponent, setHasExistingEventForComponent] = useState(false);
    // 添加/编辑思考方式对话框
    const [addThinkingMethodDialogVisible, setAddThinkingMethodDialogVisible] = useState(false);
    const [editingThinkingMethod, setEditingThinkingMethod] = useState(null);
    // 当前 eventHash（用于添加思考方式保存到数据库）
    const [currentEventHashForDialog, setCurrentEventHashForDialog] = useState("");
    // 当前页面的完整思考方法列表（后端格式，用于 update-methods 追加）
    const [currentBackendThinkingMethods, setCurrentBackendThinkingMethods] = useState([]);
    // 计算用于展示/编辑的 payload：优先使用用户编辑后的内容
    const effectivePayloadForDisplay = useMemo(() => (editedPayloadOverride ??
        lastIncomingMessage?.eventData?.payload ??
        null), [editedPayloadOverride, lastIncomingMessage]);
    // 打开「编辑传入的数据」对话框时，初始化名称、说明和 JSON 文本
    const openEditPayloadModal = useCallback(() => {
        if (!lastIncomingMessage)
            return;
        setEditPayloadModalVisible(true);
    }, [lastIncomingMessage]);
    // 当收到新的消息时，如果还没有用户自定义覆盖，则重置编辑覆盖
    useEffect(() => {
        if (!lastIncomingMessage) {
            setEditedPayloadOverride(null);
            setHasExistingEventForComponent(false);
            return;
        }
    }, [lastIncomingMessage, editedPayloadOverride]);
    // 根据传参从接口获取组件名称并显示（get-component-info）
    useEffect(() => {
        const msg = lastIncomingMessage;
        if (!msg?.source?.pageId ||
            !msg?.source?.componentId ||
            !pageId ||
            !microServiceId)
            return;
        const baseUrl = configApiBaseUrl || apiBaseUrl || "";
        const apiUrl = baseUrl
            ? `${baseUrl}/api/ai/get-component-info`
            : "/api/ai/get-component-info";
        axios
            .get(apiUrl, {
            params: {
                pageId: msg.source.pageId,
                componentId: msg.source.componentId,
            },
        })
            .then((res) => {
            const data = res?.data?.data;
            const name = data?.component_name;
            const backendEventHash = data?.event_hash;
            if (name) {
                setDisplayComponentName(name);
                setHasExistingEventForComponent(true);
            }
            else {
                setHasExistingEventForComponent(false);
            }
            if (backendEventHash && typeof backendEventHash === "string") {
                setCurrentEventHashForDialog(backendEventHash);
                const methodsUrl = baseUrl
                    ? `${baseUrl.replace(/\/$/, "")}/api/ai/get-thinking-methods`
                    : "/api/ai/get-thinking-methods";
                const methodsApiUrl = `${methodsUrl}?eventHash=${encodeURIComponent(backendEventHash)}&pageId=${encodeURIComponent(msg.source.pageId)}`;
                axios
                    .get(methodsApiUrl, {
                    headers: {
                        Authorization: msg.context?.token || "",
                    },
                })
                    .then((methodsRes) => {
                    const arr = Array.isArray(methodsRes?.data?.data)
                        ? methodsRes.data.data
                        : [];
                    setFetchedThinkingModes(arr.map((m) => ({
                        id: m.method_id || m.id,
                        label: m.display_name || m.label || m.method_id || m.id,
                        description: m.brief_desc || m.description || "",
                    })));
                    setCurrentBackendThinkingMethods(arr);
                })
                    .catch(() => {
                    setFetchedThinkingModes([]);
                });
            }
        })
            .catch(() => {
            setHasExistingEventForComponent(false);
        });
    }, [
        lastIncomingMessage?.source?.pageId,
        lastIncomingMessage?.source?.componentId,
        lastIncomingMessage?.context?.token,
        pageId,
        microServiceId,
        configApiBaseUrl,
        apiBaseUrl,
    ]);
    const { handleIncomingMessage, generateEventHash } = useAiComponentHook({
        apiBaseUrl: apiBaseUrl ?? "",
        fetchFn: typeof fetch !== "undefined" ? fetch : undefined,
    });
    useImperativeHandle(ref, () => ({
        clearSelection() {
            clearSelectionFnRef.current?.();
        },
        async receiveMessage(msg) {
            // 记录最近一次输入，供 UI 展示（不依赖 treeData）
            setLastIncomingMessage(msg);
            lastIncomingMessageRef.current = msg;
            // 收到新消息时，清空用户自定义编辑内容，避免残留
            setEditedPayloadOverride(null);
            // 用 payload 里的 label（优先 metadata.label，如页面传的中文名）更新显示名；每次收到消息都刷新，避免沿用旧英文
            const componentKey = `${msg.source?.pageId ?? ""}:${msg.source?.componentId ?? ""}`;
            displayNameComponentKeyRef.current = componentKey;
            const labelFromPayload = msg.eventData?.payload?.metadata?.label ||
                msg.eventData?.payload?.componentType ||
                msg.source.componentId;
            if (labelFromPayload && String(labelFromPayload).trim()) {
                setDisplayComponentName(String(labelFromPayload).trim());
            }
            console.log("📨 [CompsAi] 通过 ref.receiveMessage 接收消息:", {
                msgId: msg.msgId,
                source: {
                    microServiceId: msg.source.microServiceId,
                    pageId: msg.source.pageId,
                    componentId: msg.source.componentId,
                    componentType: msg.source.componentType,
                },
                eventData: {
                    eventType: msg.eventData.eventType,
                    triggerTime: msg.eventData.triggerTime,
                    payloadKeys: Object.keys(msg.eventData.payload || {}),
                    payloadSize: JSON.stringify(msg.eventData.payload || {}).length,
                },
                hasContext: !!msg.context,
                context: msg.context
                    ? {
                        userId: msg.context.userId,
                        hasToken: !!msg.context.token,
                    }
                    : null,
                fullMessage: msg,
            });
            // 新设计：先落库事件（便于后端有 event_hash），再从数据库查询思考方法用于 UI 显示
            try {
                const structuredPayload = getPayloadStructure(msg.eventData.payload);
                const eventContentStr = JSON.stringify(structuredPayload);
                let eventHash = await generateEventHash(eventContentStr);
                const baseUrl = apiBaseUrl ?? "";
                const fetchFn = typeof fetch !== "undefined" ? fetch : globalThis.fetch;
                // 先调用 update-component-info，确保该组件的传入数据已落库，后端才有 event_hash，get-thinking-methods 才能查到
                const updateInfoUrl = baseUrl
                    ? `${baseUrl.replace(/\/$/, "")}/api/ai/update-component-info`
                    : "/api/ai/update-component-info";
                try {
                    const updateRes = await fetchFn(updateInfoUrl, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: msg.context?.token || "",
                        },
                        body: JSON.stringify({
                            pageId: msg.source.pageId,
                            componentId: msg.source.componentId,
                            payload: structuredPayload,
                        }),
                    });
                    if (updateRes.ok) {
                        const updateJson = await updateRes.json().catch(() => ({}));
                        const backendEventHash = updateJson?.data?.eventHash;
                        if (typeof backendEventHash === "string" &&
                            backendEventHash.length > 0) {
                            eventHash = backendEventHash;
                        }
                        setHasExistingEventForComponent(true);
                    }
                }
                catch {
                    // 落库失败不阻断，仍用前端生成的 eventHash 尝试拉取思考方式（可能已有历史事件）
                }
                setCurrentEventHashForDialog(eventHash);
                const apiPath = baseUrl
                    ? `${baseUrl}/api/ai/get-thinking-methods`
                    : "/api/ai/get-thinking-methods";
                const apiUrl = `${apiPath}?eventHash=${encodeURIComponent(eventHash)}&pageId=${encodeURIComponent(msg.source.pageId)}`;
                console.log("📡 [CompsAi] 查询思考方法（用于 UI 显示）:", {
                    url: apiUrl,
                    componentId: msg.source.componentId,
                });
                const thinkingMethodsRes = await fetchFn(apiUrl, {
                    headers: { Authorization: msg.context?.token || "" },
                });
                if (thinkingMethodsRes.ok) {
                    const thinkingMethodsJson = await thinkingMethodsRes.json();
                    const thinkingMethods = Array.isArray(thinkingMethodsJson?.data)
                        ? thinkingMethodsJson.data.map((m) => ({
                            id: m.method_id || m.id,
                            label: m.display_name || m.label || m.method_id || m.id,
                            description: m.brief_desc || m.description || "",
                        }))
                        : thinkingMethodsJson?.data
                            ? [
                                {
                                    id: thinkingMethodsJson.data.method_id ||
                                        thinkingMethodsJson.data.id,
                                    label: thinkingMethodsJson.data.display_name ||
                                        thinkingMethodsJson.data.label ||
                                        thinkingMethodsJson.data.method_id ||
                                        thinkingMethodsJson.data.id,
                                    description: thinkingMethodsJson.data.brief_desc ||
                                        thinkingMethodsJson.data.description ||
                                        "",
                                },
                            ]
                            : [];
                    console.log("✅ [CompsAi] 思考方法查询成功:", {
                        count: thinkingMethods.length,
                        methods: thinkingMethods.map((m) => m.label),
                    });
                    setFetchedThinkingModes(thinkingMethods);
                    setCurrentBackendThinkingMethods(Array.isArray(thinkingMethodsJson?.data)
                        ? thinkingMethodsJson.data
                        : []);
                }
                else {
                    console.warn("⚠️ [CompsAi] 思考方法查询失败（不影响主流程）:", thinkingMethodsRes.status, thinkingMethodsRes.statusText);
                    setFetchedThinkingModes([]);
                }
                // 从数据库拉取组件名称（中文等），覆盖 payload 里的英文，确保「传入的信息」显示的是已保存名称
                const infoUrl = baseUrl
                    ? `${baseUrl.replace(/\/$/, "")}/api/ai/get-component-info`
                    : "/api/ai/get-component-info";
                const infoQuery = `pageId=${encodeURIComponent(msg.source.pageId)}&componentId=${encodeURIComponent(msg.source.componentId)}`;
                try {
                    const infoRes = await fetchFn(`${infoUrl}?${infoQuery}`, {
                        headers: { Authorization: msg.context?.token || "" },
                    });
                    if (infoRes.ok) {
                        const infoJson = await infoRes.json().catch(() => ({}));
                        const componentName = infoJson?.data?.component_name;
                        if (typeof componentName === "string" &&
                            componentName.trim().length > 0) {
                            setDisplayComponentName(componentName.trim());
                        }
                    }
                }
                catch {
                    // 拉取组件名称失败不阻断
                }
            }
            catch (err) {
                console.warn("⚠️ [CompsAi] 思考方法查询异常（不影响主流程）:", err);
                setFetchedThinkingModes([]);
            }
        },
    }), [
        handleIncomingMessage,
        generateEventHash,
        onResult,
        aiMessagesProp,
        loadingProp,
        apiBaseUrl,
    ]);
    // 页面配置缓存（用于获取思考模式）
    const [pageConfigCache, setPageConfigCache] = useState(null);
    const [pageConfigLoading, setPageConfigLoading] = useState(false);
    // 加载页面配置（旧设计：从 config/page 取思考模式；新设计下可跳过，思考方式来自 get-thinking-methods）
    useEffect(() => {
        if (skipPageConfigLoad ||
            !pageId ||
            !microServiceId ||
            pageConfigCache ||
            pageConfigLoading) {
            return;
        }
        setPageConfigLoading(true);
        const baseUrl = configApiBaseUrl || apiBaseUrl || "";
        const apiUrl = baseUrl
            ? `${baseUrl}/api/ai/config/page`
            : "/api/ai/config/page"; // 使用相对路径，走代理
        console.log("📡 [CompsAi] 开始加载页面配置:", {
            pageId,
            microServiceId,
            apiUrl,
        });
        axios
            .get(apiUrl, {
            params: {
                service_name: microServiceId,
                page_id: pageId,
            },
        })
            .then((response) => {
            if (response.data.code === 200) {
                console.log("✅ [CompsAi] 页面配置加载成功:", response.data.data);
                setPageConfigCache(response.data.data);
            }
            else {
                console.warn("⚠️ [CompsAi] 页面配置加载失败:", response.data.message);
            }
        })
            .catch((error) => {
            console.error("❌ [CompsAi] 页面配置加载失败:", error);
        })
            .finally(() => {
            setPageConfigLoading(false);
        });
    }, [
        skipPageConfigLoad,
        pageId,
        microServiceId,
        configApiBaseUrl,
        apiBaseUrl,
        pageConfigCache,
        pageConfigLoading,
    ]);
    // 根据 componentId 获取思考模式
    const getThinkingModesForComponent = useCallback((componentId) => {
        if (!pageConfigCache || !componentId) {
            return [];
        }
        const component = pageConfigCache.components?.find((c) => c.componentId === componentId);
        if (!component ||
            !component.thinkingModes ||
            component.thinkingModes.length === 0) {
            return [];
        }
        return component.thinkingModes
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
            .map((mode) => ({
            id: mode.thinkingModeId || mode.id,
            label: mode.label || mode.thinkingModeId || mode.id,
            description: mode.description || "",
        }));
    }, [pageConfigCache]);
    // 递归处理树节点，为每个包含 componentId 的节点添加思考模式子节点
    const enrichTreeDataWithThinkingModes = useCallback((nodes) => {
        return nodes.map((node) => {
            const enrichedNode = { ...node };
            // 如果节点有 componentId，尝试获取思考模式
            if (node.componentId && pageId && microServiceId) {
                const thinkingModes = getThinkingModesForComponent(node.componentId);
                if (thinkingModes.length > 0) {
                    // 如果节点还没有 children，或者 children 中没有思考模式节点，则添加
                    const hasThinkingChildren = enrichedNode.children?.some((child) => child.type === "thinking");
                    if (!hasThinkingChildren) {
                        const thinkingChildren = thinkingModes.map((mode) => ({
                            id: `${enrichedNode.id}-${mode.id}`,
                            label: mode.label,
                            type: "thinking",
                            description: mode.description,
                            thinkingMode: mode,
                            data: enrichedNode.data, // 继承父节点的数据
                        }));
                        enrichedNode.children = [
                            ...(enrichedNode.children || []),
                            ...thinkingChildren,
                        ];
                    }
                }
            }
            // 递归处理子节点
            if (enrichedNode.children && enrichedNode.children.length > 0) {
                enrichedNode.children = enrichTreeDataWithThinkingModes(enrichedNode.children);
            }
            return enrichedNode;
        });
    }, [pageId, microServiceId, getThinkingModesForComponent]);
    // 将旧接口（dataSource + thinkingModes）转换为 treeData
    const convertedTreeData = useMemo(() => {
        const hasTreeData = !!treeData && Array.isArray(treeData) && treeData.length > 0;
        const hasDataSource = !!dataSource && Array.isArray(dataSource) && dataSource.length > 0;
        // 无 treeData 且无 dataSource 时直接返回空数组，不打印日志（如 store-revenue-monitor 初始未传为合法场景）
        if (!hasTreeData && !hasDataSource) {
            return [];
        }
        if (process.env.NODE_ENV === "development") {
            console.log("🔄 [CompsAi] 转换 treeData:", {
                hasTreeData,
                hasDataSource,
                hasThinkingModes: !!thinkingModes &&
                    Array.isArray(thinkingModes) &&
                    thinkingModes.length > 0,
                hasPageConfig: !!pageConfigCache,
                pageId,
                microServiceId,
            });
        }
        // 如果提供了 treeData，优先使用 treeData
        if (treeData && Array.isArray(treeData) && treeData.length > 0) {
            let result = treeData;
            // 如果提供了 pageId 和 microServiceId，并且页面配置已加载，则动态添加思考模式
            if (pageId && microServiceId && pageConfigCache) {
                console.log("✅ [CompsAi] 使用 treeData 并动态添加思考模式");
                result = enrichTreeDataWithThinkingModes(treeData);
            }
            else {
                console.log("✅ [CompsAi] 使用 treeData，节点数量:", treeData.length);
            }
            return result;
        }
        // 否则，尝试从 dataSource 和 thinkingModes 构建 treeData
        if (dataSource && Array.isArray(dataSource) && dataSource.length > 0) {
            const safeThinkingModes = thinkingModes &&
                Array.isArray(thinkingModes) &&
                thinkingModes.length > 0
                ? thinkingModes
                : [];
            console.log("✅ [CompsAi] 从 dataSource 构建 treeData:", {
                dataSourceCount: dataSource.length,
                thinkingModesCount: safeThinkingModes.length,
            });
            return dataSource.map((item) => {
                const dataNode = {
                    id: item.id,
                    label: item.label,
                    type: "data",
                    data: item.value,
                    children: safeThinkingModes.length > 0
                        ? safeThinkingModes.map((mode) => ({
                            id: `${item.id}-${mode.id}`,
                            label: mode.label,
                            type: "thinking",
                            description: mode.description,
                            thinkingMode: mode,
                            data: item.value,
                        }))
                        : undefined,
                };
                return dataNode;
            });
        }
        // 理论上不会走到这里（无数据已在开头提前 return []）
        return [];
    }, [
        treeData,
        dataSource,
        thinkingModes,
        pageConfigCache,
        pageId,
        microServiceId,
        enrichTreeDataWithThinkingModes,
    ]);
    // 根据节点类型获取默认图标
    const getNodeIcon = (node) => {
        if (node.icon) {
            return node.icon;
        }
        if (node.type === "data") {
            return _jsx(DatabaseOutlined, { style: { fontSize: 16, color: "#1890ff" } });
        }
        if (node.type === "thinking") {
            return _jsx(ThunderboltOutlined, { style: { fontSize: 14, color: "#52c41a" } });
        }
        // 默认图标
        return _jsx(DatabaseOutlined, { style: { fontSize: 14, color: "#666" } });
    };
    // 递归构建 Ant Design Tree 数据格式
    const buildTreeData = (nodes, path = []) => {
        return nodes.map((node) => {
            const currentPath = [...path, node];
            // 使用自定义渲染函数或默认渲染
            const title = renderNodeTitle ? (renderNodeTitle(node)) : (_jsxs(Space, { style: { width: "100%" }, children: [node.type !== "thinking" && getNodeIcon(node), node.type !== "thinking" && (_jsx(Text, { style: { fontSize: node.type === "data" ? 16 : 14 }, children: node.label })), node.description && (_jsx(Text, { style: { fontSize: 12, color: "#666" }, children: node.description })), node.type === "thinking" && getNodeIcon(node)] }));
            const treeNode = {
                title,
                key: node.id,
                treeNode: node,
                path: currentPath, // 保存路径信息
            };
            // 递归处理子节点
            // 确保创建新的数组引用，避免数据累积
            if (node.children &&
                Array.isArray(node.children) &&
                node.children.length > 0) {
                treeNode.children = buildTreeData([...node.children], currentPath);
            }
            else {
                // 明确设置为 undefined，避免残留的子节点
                treeNode.children = undefined;
            }
            return treeNode;
        });
    };
    // 构建树形数据
    // 过滤掉顶层的独立思考方式节点（只显示有数据关联的节点）
    const antdTreeData = useMemo(() => {
        // 检查 convertedTreeData 是否存在且为数组
        if (!convertedTreeData ||
            !Array.isArray(convertedTreeData) ||
            convertedTreeData.length === 0) {
            return [];
        }
        // 过滤掉顶层的思考方式节点（type === 'thinking'）
        // 思考方式节点应该只作为数据节点的子节点显示，不应该出现在顶层
        const filteredTreeData = convertedTreeData.filter((node) => {
            // 只保留数据节点（type === 'data'），过滤掉所有顶层的思考方式节点
            return node.type !== "thinking";
        });
        // 每次构建时都从全新的数据构建，避免数据累积
        return buildTreeData(filteredTreeData);
    }, [convertedTreeData, renderNodeTitle]);
    // 生成树组件的 key，基于数据内容的哈希，确保数据变化时组件重新渲染
    const treeKey = antdTreeData.length === 0
        ? "empty-tree"
        : antdTreeData.map((node) => node.key).join("-");
    // 树形结构的选择状态管理
    const [selectedKeys, setSelectedKeys] = useState([]);
    // 当前选择的信息（用于清理）
    const [currentSelection, setCurrentSelection] = useState(null);
    // 当 treeData 变为空时，自动清理选择状态
    useEffect(() => {
        if (!convertedTreeData || convertedTreeData.length === 0) {
            setSelectedKeys([]);
            setCurrentSelection(null);
        }
    }, [convertedTreeData]);
    // 切换组件/门店时清理选择状态（由 ref.clearSelection 调用）
    // 注意：不清空 setFetchedThinkingModes，避免在 receiveMessage 异步拉取新思考方式前出现空白；新数据会覆盖
    const handleClearSelection = useCallback(() => {
        setSelectedKeys([]);
        setCurrentSelection(null);
        setLastIncomingMessage(null);
        lastIncomingMessageRef.current = null;
        setEditedPayloadOverride(null);
        setDisplayComponentName("");
        displayNameComponentKeyRef.current = null;
        setCurrentEventHashForDialog("");
        setCurrentBackendThinkingMethods([]);
        if (aiMessagesProp === undefined)
            setInnerAiMessages([]);
        if (loadingProp === undefined)
            setInnerLoading(false);
    }, [aiMessagesProp, loadingProp]);
    useEffect(() => {
        clearSelectionFnRef.current = handleClearSelection;
        return () => {
            clearSelectionFnRef.current = () => { };
        };
    }, [handleClearSelection]);
    // 第二部分「可选择的思考方式」所用列表：
    // 新设计：优先使用从数据库查询的思考方法（基于 lastIncomingMessage）
    // 旧设计：从树节点或页面配置中获取
    // 仅有在用户已选择/点击组件（有 lastIncomingMessage 或树选择）时才展示思考方式
    const currentThinkingModesForPart2 = useMemo(() => {
        const hasContext = lastIncomingMessage || (convertedTreeData?.length ?? 0) > 0;
        if (!hasContext) {
            return [];
        }
        // 新设计：如果有从数据库查询到的思考方法，优先使用
        if (fetchedThinkingModes.length > 0) {
            return fetchedThinkingModes;
        }
        // 旧设计：从树节点或页面配置中获取
        const path = currentSelection?.path;
        const dataNode = path?.length
            ? [...path].reverse().find((n) => n.type === "data")
            : convertedTreeData?.[0];
        const node = dataNode ?? convertedTreeData?.[0];
        const cid = node && "componentId" in node
            ? node.componentId
            : lastIncomingMessage?.source.componentId; // 新设计：如果没有树节点，尝试从 lastIncomingMessage 获取
        if (cid && getThinkingModesForComponent) {
            return getThinkingModesForComponent(cid);
        }
        if (thinkingModes &&
            Array.isArray(thinkingModes) &&
            thinkingModes.length > 0) {
            return thinkingModes;
        }
        return [];
    }, [
        lastIncomingMessage,
        convertedTreeData,
        fetchedThinkingModes,
        currentSelection?.path,
        getThinkingModesForComponent,
        thinkingModes,
        lastIncomingMessage?.source.componentId,
    ]);
    // 处理树节点点击
    const handleTreeSelect = async (selectedKeys, info) => {
        console.log("🖱️ [CompsAi] 树节点点击:", {
            selectedKeys,
            nodeInfo: {
                id: info.node?.key,
                title: info.node?.title,
                hasTreeNode: !!info.node?.treeNode,
                hasPath: !!info.node?.path,
                pathLength: info.node?.path?.length || 0,
            },
            fullInfo: info,
        });
        // 更新选择状态
        setSelectedKeys(selectedKeys);
        const node = info.node.treeNode;
        const path = info.node.path || [node];
        if (!node) {
            console.warn("⚠️ [CompsAi] 节点为空，无法处理点击");
            return;
        }
        console.log("📋 [CompsAi] 解析后的节点信息:", {
            node: {
                id: node.id,
                label: node.label,
                type: node.type,
                hasData: !!node.data,
                hasThinkingMode: !!node.thinkingMode,
                thinkingModeId: node.thinkingMode?.id,
                hasChildren: !!node.children,
                childrenCount: node.children?.length || 0,
            },
            path: path.map((p) => ({
                id: p.id,
                label: p.label,
                type: p.type,
            })),
        });
        // 如果是思考方式节点，且提供了旧接口的回调
        if (node.type === "thinking" &&
            node.thinkingMode &&
            onSelectionChange &&
            onFetchData) {
            // 找到对应的数据源项
            const dataSourceItem = dataSource?.find((item) => {
                // 通过路径找到数据节点
                const dataNode = path.find((n) => n.type === "data");
                return (dataNode && (item.id === dataNode.id || item.label === dataNode.label));
            });
            if (dataSourceItem) {
                try {
                    // 获取数据
                    const selectedData = await onFetchData(dataSourceItem);
                    // 调用旧接口的回调
                    onSelectionChange({
                        dataSourceItem,
                        selectedData,
                        thinkingMode: node.thinkingMode,
                        selectedFields: [],
                    });
                }
                catch (error) {
                    console.error("获取数据失败:", error);
                }
            }
            return;
        }
        // 保存当前选择信息（用于清理）
        setCurrentSelection({
            nodeId: node.id,
            path,
            thinkingMode: node.thinkingMode,
        });
        // 优先使用 onNodeClick
        if (onNodeClick) {
            console.log("📤 [CompsAi] 调用 onNodeClick 回调:", {
                nodeId: node.id,
                nodeLabel: node.label,
                nodeType: node.type,
                thinkingModeId: node.thinkingMode?.id,
                pathLength: path.length,
                hasData: !!node.data,
                params: {
                    node,
                    path,
                    data: node.data,
                    thinkingMode: node.thinkingMode,
                },
            });
            onNodeClick({
                node,
                path,
                data: node.data,
                thinkingMode: node.thinkingMode,
            });
        }
        else if (onThinkingModeClick && node.thinkingMode) {
            // 向后兼容：如果是思考方式节点，调用旧的回调
            console.log("📤 [CompsAi] 调用 onThinkingModeClick 回调（向后兼容）:", {
                thinkingModeId: node.thinkingMode.id,
                thinkingModeLabel: node.thinkingMode.label,
                hasData: !!node.data,
                params: {
                    data: node.data,
                    thinkingMode: node.thinkingMode,
                    node,
                    path,
                },
            });
            onThinkingModeClick({
                data: node.data,
                thinkingMode: node.thinkingMode,
                node,
                path,
            });
        }
        else {
            console.warn("⚠️ [CompsAi] 没有提供 onNodeClick 或 onThinkingModeClick 回调");
        }
    };
    // 第二部分「思考方式」点击时：
    // 1）如果有树选择，则通过回调把数据 + 思考方式抛给外部；
    // 2）只要有最近一次消息，就调用 AI（handleIncomingMessage），将结果写入反馈结果。
    const handlePart2ThinkingModeClick = useCallback((mode) => {
        // Step 1：有树选中时，仍然把事件抛给外部（向后兼容）
        if (currentSelection?.path?.length) {
            const lastData = [...currentSelection.path]
                .reverse()
                .find((n) => n.type === "data");
            const path = currentSelection.path ?? [];
            if (lastData?.data != null) {
                onThinkingModeClick?.({
                    data: lastData.data,
                    thinkingMode: mode,
                    node: lastData,
                    path,
                });
                onNodeClick?.({
                    node: lastData,
                    path,
                    data: lastData.data,
                    thinkingMode: mode,
                });
            }
        }
        // 优先用 ref 中的最新消息（带完整 payload），避免 state 未刷新时只传结构不传数据
        const msgToSend = lastIncomingMessageRef.current ?? lastIncomingMessage;
        if (!msgToSend) {
            console.warn("⚠️ [CompsAi] 无法触发 AI 调用：缺少最近一次传入消息(lastIncomingMessage)");
            return;
        }
        (async () => {
            try {
                if (loadingProp === undefined) {
                    setInnerLoading(true);
                }
                // 按流程图：点击思考方式时先整理哈希查缓存，无缓存再调 AI；只处理当前点击的思考方式
                // 传入 currentEventHashForDialog 与库中事件一致，避免「未找到思考方法」
                const results = await handleIncomingMessage(msgToSend, mode.id, undefined, currentEventHashForDialog || undefined);
                console.log("✅ [CompsAi] 点击思考方式，AI 返回结果:", {
                    thinkingModeId: mode.id,
                    resultKeys: Object.keys(results || {}),
                    resultCount: Object.keys(results || {}).length,
                });
                onResult?.(results);
                if (aiMessagesProp === undefined) {
                    const ts = Date.now();
                    const displayContent = getDisplayContentFromResults(results);
                    setInnerAiMessages((prev) => [
                        ...prev,
                        {
                            id: `result-${msgToSend.msgId}-${mode.id}-${ts}`,
                            content: displayContent || JSON.stringify(results, null, 2),
                            timestamp: ts,
                            thinkingMode: {
                                id: mode.id,
                                label: mode.label,
                                description: mode.description,
                            },
                        },
                    ]);
                }
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.error("❌ [CompsAi] 点击思考方式触发 AI 调用失败:", err);
                onResult?.({ error: err.message });
                if (aiMessagesProp === undefined) {
                    const ts = Date.now();
                    setInnerAiMessages((prev) => [
                        ...prev,
                        {
                            id: `error-${msgToSend.msgId}-${mode.id}-${ts}`,
                            content: `处理失败：${err?.message || String(err)}`,
                            timestamp: ts,
                            thinkingMode: {
                                id: mode.id,
                                label: mode.label,
                                description: mode.description,
                            },
                        },
                    ]);
                }
            }
            finally {
                if (loadingProp === undefined) {
                    setInnerLoading(false);
                }
            }
        })();
    }, [
        currentSelection?.path,
        onThinkingModeClick,
        onNodeClick,
        lastIncomingMessage,
        loadingProp,
        aiMessagesProp,
        handleIncomingMessage,
        onResult,
        currentEventHashForDialog,
    ]);
    // 第二部分思考方式项点击：Shift+Ctrl(Win/Linux) 或 Shift+Command(Mac)+点击进入编辑，否则执行思考方式（调 AI）；组合键为隐藏方式，无 tip
    const handleThinkingModeItemClick = useCallback((mode, e) => {
        if (e.shiftKey && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            e.stopPropagation();
            if (onEditThinkingMode) {
                onEditThinkingMode(mode);
            }
            else {
                const full = currentBackendThinkingMethods.find((m) => m.method_id === mode.id);
                if (full) {
                    setEditingThinkingMethod(full);
                    setAddThinkingMethodDialogVisible(true);
                }
            }
        }
        else {
            handlePart2ThinkingModeClick(mode);
        }
    }, [
        onEditThinkingMode,
        currentBackendThinkingMethods,
        handlePart2ThinkingModeClick,
    ]);
    // 处理清理操作
    const handleClear = useCallback(() => {
        console.log("🧹 [CompsAi] 清理按钮被点击:", {
            hasOnClearAll: !!onClearAll,
            hasOnClearMessages: !!onClearMessages,
            currentSelectedKeys: selectedKeys,
            hasCurrentSelection: !!currentSelection,
        });
        // 优先使用 onClearAll（全面清理）
        if (onClearAll) {
            console.log("✅ [CompsAi] 调用 onClearAll（全面清理）");
            onClearAll();
        }
        else if (onClearMessages) {
            // 向后兼容：只清理消息
            console.log("✅ [CompsAi] 调用 onClearMessages（仅清理消息）");
            onClearMessages();
        }
        else {
            console.warn("⚠️ [CompsAi] 没有提供清理回调函数");
        }
        // 非受控模式：清理组件内部消息/加载态、组件名称
        if (aiMessagesProp === undefined) {
            setInnerAiMessages([]);
        }
        if (loadingProp === undefined) {
            setInnerLoading(false);
        }
        setEditedPayloadOverride(null);
        setDisplayComponentName("");
        displayNameComponentKeyRef.current = null;
        lastIncomingMessageRef.current = null;
        setLastIncomingMessage(null);
        setFetchedThinkingModes([]);
        setCurrentEventHashForDialog("");
        setCurrentBackendThinkingMethods([]);
        // 清理选择状态
        setSelectedKeys([]);
        setCurrentSelection(null);
        console.log("✅ [CompsAi] 已清理选择状态");
    }, [
        onClearAll,
        onClearMessages,
        selectedKeys,
        currentSelection,
        aiMessagesProp,
        loadingProp,
    ]);
    /** 仅清空「反馈结果」列表，不清空传入信息、思考方式、选择状态等 */
    const handleClearFeedbackOnly = useCallback(() => {
        if (aiMessagesProp === undefined) {
            setInnerAiMessages([]);
        }
        onClearMessages?.();
    }, [aiMessagesProp, onClearMessages]);
    /** 刷新：先清空返回结果内容，再重新执行最近一次思考方式并从后端获取 */
    const handleRefreshFeedback = useCallback(() => {
        const lastMessage = aiMessages[aiMessages.length - 1];
        const lastMode = lastMessage?.thinkingMode;
        const msgToSend = lastIncomingMessageRef.current ?? lastIncomingMessage;
        if (!msgToSend || !lastMode)
            return;
        // 先清空反馈结果内容
        if (aiMessagesProp === undefined) {
            setInnerAiMessages([]);
        }
        onClearMessages?.();
        if (loadingProp === undefined)
            setInnerLoading(true);
        (async () => {
            try {
                const results = await handleIncomingMessage(msgToSend, lastMode.id, true, currentEventHashForDialog || undefined);
                onResult?.(results);
                if (aiMessagesProp === undefined) {
                    const displayContent = getDisplayContentFromResults(results);
                    const ts = Date.now();
                    setInnerAiMessages([
                        {
                            id: `result-${msgToSend.msgId}-${lastMode.id}-${ts}`,
                            content: displayContent || JSON.stringify(results, null, 2),
                            timestamp: ts,
                            thinkingMode: {
                                id: lastMode.id,
                                label: lastMode.label,
                                description: lastMode.description,
                            },
                        },
                    ]);
                }
            }
            catch (err) {
                console.error("❌ [CompsAi] 刷新反馈结果失败:", err);
                onResult?.({ error: err.message });
                if (aiMessagesProp === undefined) {
                    const ts = Date.now();
                    setInnerAiMessages([
                        {
                            id: `error-${msgToSend.msgId}-${lastMode.id}-${ts}`,
                            content: `刷新失败：${err?.message || String(err)}`,
                            timestamp: ts,
                            thinkingMode: {
                                id: lastMode.id,
                                label: lastMode.label,
                                description: lastMode.description,
                            },
                        },
                    ]);
                }
            }
            finally {
                if (loadingProp === undefined)
                    setInnerLoading(false);
            }
        })();
    }, [
        aiMessages,
        lastIncomingMessage,
        loadingProp,
        handleIncomingMessage,
        onResult,
        aiMessagesProp,
        currentEventHashForDialog,
    ]);
    // 构建按钮配置 - 使用 ContainerPanel 的内置功能
    const buttons = useMemo(() => {
        const btnConfigs = [];
        if (onSettingsClick) {
            btnConfigs.push({
                key: "settings",
                icon: _jsx(SettingOutlined, {}),
                type: "text",
                onClick: onSettingsClick,
                title: "设置",
                showTextOnHover: true, // 使用 ContainerPanel 的悬停显示文字功能
            });
        }
        // 加载旋钮已移至「反馈结果」标题行显示，此处不再添加
        // 清理按钮：优先使用 onClearAll，否则使用 onClearMessages（向后兼容）
        if (onClearAll || onClearMessages) {
            btnConfigs.push({
                key: "clear",
                icon: _jsx(ClearOutlined, {}),
                type: "text",
                onClick: handleClear,
                disabled: loading,
                title: onClearAll ? "清空所有（消息、选择、返回值、缓存）" : "清空消息",
                showTextOnHover: true, // 使用 ContainerPanel 的悬停显示文字功能
            });
        }
        return btnConfigs;
    }, [onSettingsClick, onClearAll, onClearMessages, loading, handleClear]);
    return (_jsxs("div", { style: {
            position: "relative",
            ...style,
            height: height === "100%" ? "100%" : height,
        }, children: [_jsx(ContainerPanel, { title: title, height: height, buttons: buttons, contentPadding: 0, cardProps: {
                    styles: {
                        body: {
                            height: height === "100%" ? "calc(100% - 57px)" : undefined,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            paddingTop: "0px",
                            paddingBottom: "0px",
                            paddingLeft: "0px",
                            paddingRight: "0px",
                        },
                    },
                }, children: _jsxs("div", { style: {
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        gap: 0,
                        overflow: "hidden",
                    }, children: [_jsxs("div", { style: {
                                flex: "0 0 auto",
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 0,
                            }, children: [_jsx("div", { style: {
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        margin: "0 0 8px",
                                    }, children: _jsx(Title, { level: 5, style: {
                                            margin: 0,
                                            fontSize: 12,
                                            fontWeight: 200,
                                            color: "#595959",
                                        }, children: _jsx("span", { onClick: (e) => {
                                                if (e.shiftKey && (e.ctrlKey || e.metaKey)) {
                                                    e.preventDefault();
                                                    if (lastIncomingMessage)
                                                        openEditPayloadModal();
                                                }
                                            }, children: "\u4F20\u5165\u7684\u4FE1\u606F" }) }) }), _jsx("div", { style: { padding: "0" }, children: antdTreeData.length > 0 ? (_jsx(Tree, { treeData: antdTreeData, showLine: false, defaultExpandAll: defaultExpandAll, selectedKeys: selectedKeys, onSelect: handleTreeSelect, blockNode: true, style: { backgroundColor: "transparent" } }, treeKey)) : lastIncomingMessage ? (_jsx("div", { style: { padding: "0 0 8px" }, children: _jsxs("div", { style: {
                                                padding: 12,
                                                backgroundColor: "#fafafa",
                                                borderRadius: 4,
                                                border: COMPONENT_OUTLINE_BORDER,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 4,
                                            }, children: [_jsx(Text, { style: {
                                                        fontSize: 14,
                                                        fontWeight: 500,
                                                        color: "#262626",
                                                    }, children: displayComponentName ||
                                                        effectivePayloadForDisplay?.metadata?.label ||
                                                        effectivePayloadForDisplay?.componentType ||
                                                        lastIncomingMessage.source.componentId }), effectivePayloadForDisplay?.metadata?.storeName && (_jsx(Text, { style: {
                                                        fontSize: 12,
                                                        fontWeight: 200,
                                                        color: "#8c8c8c",
                                                    }, children: effectivePayloadForDisplay.metadata.storeName }))] }) })) : null })] }), _jsx(Divider, { style: { margin: "8px 0" } }), _jsxs("div", { style: {
                                flex: "0 0 auto",
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 0,
                            }, children: [_jsx("div", { style: {
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        margin: "4px 0 4px",
                                    }, children: _jsx(Title, { level: 5, style: {
                                            margin: 0,
                                            fontSize: 12,
                                            fontWeight: 200,
                                            color: "#595959",
                                        }, children: _jsx("span", { onClick: (e) => {
                                                if (e.shiftKey && (e.ctrlKey || e.metaKey)) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    if (onAddThinkingMode) {
                                                        onAddThinkingMode();
                                                    }
                                                    else {
                                                        setEditingThinkingMethod(null);
                                                        setAddThinkingMethodDialogVisible(true);
                                                    }
                                                }
                                            }, children: "\u601D\u8003\u65B9\u5F0F" }) }) }), currentThinkingModesForPart2.length > 0 ? (_jsx("div", { style: {
                                        padding: "0 0 8px",
                                        maxHeight: 160,
                                        overflow: "auto",
                                    }, children: _jsx("div", { style: {
                                            padding: 12,
                                            backgroundColor: "#fafafa",
                                            borderRadius: 4,
                                            border: COMPONENT_OUTLINE_BORDER,
                                            minHeight: 44,
                                        }, children: _jsx(List, { className: "comps-ai-thinking-modes-list", size: "small", dataSource: currentThinkingModesForPart2, style: { marginBottom: 0 }, renderItem: (mode) => (_jsx(List.Item, { style: { padding: "4px 0" }, children: _jsx(List.Item.Meta, { title: _jsx(Text, { style: { cursor: "pointer" }, onClick: (e) => handleThinkingModeItemClick(mode, e), children: mode.label }), description: _jsxs(_Fragment, { children: [mode.description ? (_jsx(Text, { type: "secondary", style: { fontSize: 12 }, children: mode.description })) : null, lastIncomingMessage?.eventData?.triggerTime && (_jsx(Text, { style: {
                                                                    display: "block",
                                                                    marginTop: 4,
                                                                    fontSize: 12,
                                                                    fontWeight: 200,
                                                                    color: "#8c8c8c",
                                                                }, children: new Date(lastIncomingMessage.eventData.triggerTime).toLocaleString("zh-CN", {
                                                                    timeZone: "Asia/Shanghai",
                                                                    year: "numeric",
                                                                    month: "2-digit",
                                                                    day: "2-digit",
                                                                    hour: "2-digit",
                                                                    minute: "2-digit",
                                                                    second: "2-digit",
                                                                }) }))] }) }) }, mode.id)) }) }) })) : null] }), _jsx(Divider, { style: { margin: "8px 0" } }), _jsxs("div", { style: {
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 120,
                                overflow: "hidden",
                            }, children: [_jsxs("div", { style: {
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        margin: "4px 0 8px",
                                    }, children: [_jsx(Title, { level: 5, style: {
                                                margin: 0,
                                                fontSize: 12,
                                                fontWeight: 200,
                                                color: "#595959",
                                            }, children: "\u53CD\u9988\u7ED3\u679C" }), loading ? (_jsx(LoadingOutlined, { spin: true, style: { color: "#1890ff", fontSize: 14 } })) : aiMessages.length > 0 ? (_jsxs(Space, { size: 4, children: [_jsx(Button, { type: "link", size: "small", icon: _jsx(ReloadOutlined, {}), onClick: handleRefreshFeedback, disabled: loading || !lastIncomingMessage || aiMessages.length === 0, style: { padding: "0 4px", fontSize: 12 }, children: "\u5237\u65B0" }), _jsx(Button, { type: "link", size: "small", icon: _jsx(ClearOutlined, {}), onClick: handleClearFeedbackOnly, disabled: loading, style: { padding: "0 4px", fontSize: 12 }, children: "\u6E05\u7406" })] })) : null] }), _jsx("div", { style: {
                                        flex: 1,
                                        padding: "0 0 12px",
                                        borderRadius: 4,
                                        overflow: "auto",
                                        minHeight: 100,
                                    }, children: aiMessages.length === 0 ? null : (_jsx("div", { style: { display: "flex", flexDirection: "column", gap: 24 }, children: [...aiMessages].reverse().map((message) => (_jsxs("div", { style: {
                                                padding: "12px",
                                                backgroundColor: "#fafafa",
                                                borderRadius: 4,
                                            }, children: [_jsxs("div", { style: {
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 4,
                                                        marginBottom: 8,
                                                    }, children: [_jsx(Text, { type: "secondary", style: { fontSize: 12 }, children: message.thinkingMode?.label ?? "未知" }), _jsx(Text, { style: {
                                                                fontSize: 12,
                                                                fontWeight: 200,
                                                                color: "#8c8c8c",
                                                            }, children: new Date(message.timestamp).toLocaleString("zh-CN", {
                                                                timeZone: "Asia/Shanghai",
                                                                year: "numeric",
                                                                month: "2-digit",
                                                                day: "2-digit",
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                                second: "2-digit",
                                                            }) })] }), _jsx(Paragraph, { style: {
                                                        margin: 0,
                                                        whiteSpace: "pre-wrap",
                                                        wordBreak: "break-word",
                                                        fontSize: 13,
                                                    }, children: renderContentWithFormatting(getDisplayContent(message.content)) })] }, message.id))) })) })] })] }) }), _jsx(AddThinkingMethodDialog, { visible: addThinkingMethodDialogVisible, onClose: () => {
                    setAddThinkingMethodDialogVisible(false);
                    setEditingThinkingMethod(null);
                }, initialMethod: editingThinkingMethod, onSaved: () => {
                    if (!lastIncomingMessage)
                        return;
                    const pageIdForFetch = lastIncomingMessage.source.pageId;
                    const tokenForFetch = lastIncomingMessage.context?.token || "";
                    const doFetch = (eventHash) => {
                        const baseUrl = apiBaseUrl ?? "";
                        const apiPath = baseUrl
                            ? `${baseUrl}/api/ai/get-thinking-methods`
                            : "/api/ai/get-thinking-methods";
                        const apiUrl = `${apiPath}?eventHash=${encodeURIComponent(eventHash)}&pageId=${encodeURIComponent(pageIdForFetch)}`;
                        (typeof fetch !== "undefined" ? fetch : globalThis.fetch)(apiUrl, {
                            headers: { Authorization: tokenForFetch },
                        })
                            .then((r) => (r.ok ? r.json() : { data: [] }))
                            .then((res) => {
                            const arr = Array.isArray(res?.data) ? res.data : [];
                            setFetchedThinkingModes(arr.map((m) => ({
                                id: m.method_id || m.id,
                                label: m.display_name || m.label || m.method_id || m.id,
                                description: m.brief_desc || m.description || "",
                            })));
                            setCurrentBackendThinkingMethods(arr);
                        })
                            .catch(() => { });
                    };
                    if (currentEventHashForDialog) {
                        doFetch(currentEventHashForDialog);
                    }
                    else {
                        const structuredPayload = getPayloadStructure(lastIncomingMessage.eventData.payload);
                        generateEventHash(JSON.stringify(structuredPayload)).then(doFetch);
                    }
                }, pageId: lastIncomingMessage?.source?.pageId || pageId || "", eventHash: currentEventHashForDialog, token: lastIncomingMessage?.context?.token, apiBaseUrl: apiBaseUrl, existingMethods: currentBackendThinkingMethods, samplePayload: effectivePayloadForDisplay || null }), _jsx(ContainerModal, { title: "\u7F16\u8F91\u4F20\u5165\u7684\u6570\u636E", visible: editPayloadModalVisible, width: 720, onCancel: () => {
                    if (editPayloadSaving)
                        return;
                    setEditPayloadModalVisible(false);
                }, footerRightButtons: [
                    {
                        key: "cancel",
                        text: "取消",
                        onClick: () => {
                            if (editPayloadSaving)
                                return;
                            setEditPayloadModalVisible(false);
                        },
                    },
                    {
                        key: "ok",
                        text: "确定",
                        type: "primary",
                        loading: editPayloadSaving,
                        onClick: async () => {
                            if (!lastIncomingMessage) {
                                setEditPayloadModalVisible(false);
                                return;
                            }
                            const formValues = editPayloadFormRef.current?.getFieldsValue?.() ?? {};
                            const nameFromForm = formValues.name ?? "";
                            const descFromForm = formValues.description ?? "";
                            // 弹窗内只展示结构、不编辑数据；
                            // 保存到后端时：eventHash / ai_page_event 只基于「结构」，不包含具体酒店/业务数据
                            const fullPayload = effectivePayloadForDisplay ??
                                lastIncomingMessage.eventData?.payload ??
                                {};
                            const nextPayload = {
                                ...fullPayload,
                                metadata: {
                                    ...(fullPayload?.metadata || {}),
                                    ...(nameFromForm ? { label: nameFromForm } : {}),
                                    ...(descFromForm ? { description: descFromForm } : {}),
                                },
                            };
                            setEditedPayloadOverride(nextPayload);
                            if (nameFromForm) {
                                setDisplayComponentName(nameFromForm);
                            }
                            const baseUrl = apiBaseUrl ?? "";
                            const apiUrl = baseUrl
                                ? `${baseUrl}/api/ai/update-component-info`
                                : "/api/ai/update-component-info";
                            // 仅将结构化后的 payload 传给后端，用于生成 event_hash 与落库
                            const structuredPayload = getPayloadStructure(nextPayload);
                            const body = {
                                pageId: lastIncomingMessage.source.pageId || pageId,
                                componentId: lastIncomingMessage.source.componentId,
                                componentName: nameFromForm ||
                                    displayComponentName ||
                                    nextPayload?.metadata?.label ||
                                    lastIncomingMessage.source.componentId,
                                description: descFromForm,
                                payload: structuredPayload,
                            };
                            try {
                                setEditPayloadSaving(true);
                                const fetchFn = typeof fetch !== "undefined" ? fetch : globalThis.fetch;
                                const res = await fetchFn(apiUrl, {
                                    method: "POST",
                                    headers: {
                                        "Content-Type": "application/json",
                                        Authorization: lastIncomingMessage.context?.token || "",
                                    },
                                    body: JSON.stringify(body),
                                });
                                if (!res.ok) {
                                    const errorText = await res.text().catch(() => "");
                                    throw new Error(errorText || res.statusText || "保存失败（服务器返回 500）");
                                }
                                const resJson = await res.json().catch(() => ({}));
                                const backendEventHash = resJson?.data?.eventHash;
                                if (backendEventHash && typeof backendEventHash === "string") {
                                    setCurrentEventHashForDialog(backendEventHash);
                                }
                                message.success("已保存到组件配置");
                                // 一旦成功保存传入信息，可认为事件已在后端存在，允许添加思考方式
                                setHasExistingEventForComponent(true);
                                setEditPayloadModalVisible(false);
                            }
                            catch (e) {
                                // eslint-disable-next-line no-console
                                console.error("❌ [CompsAi] 保存编辑传入的信息失败:", e, {
                                    apiUrl,
                                    body,
                                });
                                message.error("无法保存编辑传入的信息，请稍后重试或联系后台同学检查接口。");
                            }
                            finally {
                                setEditPayloadSaving(false);
                            }
                        },
                    },
                ], contentPadding: { top: 32, bottom: 32, left: 24, right: 24 }, children: _jsx("div", { style: { display: "flex", flexDirection: "column", gap: 12 }, children: _jsx(CompsForm, { formItems: [
                            {
                                name: "name",
                                label: "名称",
                                type: "input",
                                colSpan: 24,
                                props: {
                                    placeholder: "请输入组件名称（用于展示与配置）",
                                },
                            },
                            {
                                name: "description",
                                label: "说明",
                                type: "input",
                                colSpan: 24,
                                props: {
                                    placeholder: "请输入组件说明，便于后续在配置与管理界面中识别",
                                },
                            },
                            {
                                name: "payload",
                                label: "传送信息",
                                type: "custom",
                                colSpan: 24,
                                customComponent: () => {
                                    const basePayload = effectivePayloadForDisplay ??
                                        lastIncomingMessage?.eventData?.payload ??
                                        {};
                                    let structureJson = "";
                                    try {
                                        structureJson = JSON.stringify(getPayloadStructure(basePayload), null, 2);
                                    }
                                    catch {
                                        structureJson = "// 无法抽取结构";
                                    }
                                    return (_jsx("div", { style: {
                                            borderRadius: 6,
                                            border: COMPONENT_OUTLINE_BORDER,
                                            height: 260,
                                            overflowY: "auto",
                                            padding: 4,
                                        }, children: _jsx(Input.TextArea, { value: structureJson, readOnly: true, autoSize: false, style: {
                                                border: "none",
                                                boxShadow: "none",
                                                resize: "none",
                                                height: "100%",
                                                fontFamily: "Menlo, Monaco, Consolas, monospace",
                                                fontSize: 12,
                                            }, placeholder: "// \u4EC5\u5C55\u793A\u7ED3\u6784\uFF08\u5B57\u6BB5\u4E0E\u5C42\u7EA7\uFF09\uFF0C\u4E0D\u5305\u542B\u5177\u4F53\u6570\u636E\uFF1B\u53EF\u6839\u636E\u6B64\u7ED3\u6784\u51B3\u5B9A\u662F\u5426\u4FDD\u5B58\u4E3A\u5F85\u54CD\u5E94\u4E8B\u4EF6" }) }));
                                },
                            },
                        ], formRef: (form) => {
                            editPayloadFormRef.current = form;
                        }, initialValues: useMemo(() => {
                            const basePayload = effectivePayloadForDisplay ??
                                lastIncomingMessage?.eventData?.payload ??
                                {};
                            const rawNameFromPayload = basePayload?.metadata?.label ||
                                basePayload?.componentType ||
                                lastIncomingMessage?.source.componentId;
                            const name = displayComponentName && displayComponentName.trim().length > 0
                                ? displayComponentName
                                : rawNameFromPayload;
                            const descFromPayload = basePayload?.metadata?.description || "";
                            return {
                                name: String(name || ""),
                                description: String(descFromPayload || ""),
                            };
                        }, [
                            effectivePayloadForDisplay,
                            lastIncomingMessage,
                            displayComponentName,
                        ]), onValuesChange: undefined, layout: "horizontal", labelCol: { span: 4 }, wrapperCol: { span: 20 }, contentPadding: 0 }) }) })] }));
};
const CompsAi = forwardRef(CompsAiInner);
export default CompsAi;
export { generateMsgId, formatTriggerTime } from "./aiComponentTypes";
